/**
 * AssistantDock — the assistant on every page (R4 §2.8).
 *
 * Mounted once in AppLayout, to the right of the page. On a bid page it hosts
 * that bid's conversation (Tender Chat, `tender-chat-store`); on every other
 * page it hosts the org assistant (`assistant-store`, the Assistant page's
 * threads). Hidden on the five editor pages, which keep UnifiedAiPanel, and on
 * the Assistant page itself, so the org conversation is never mounted twice.
 *
 * It docks and pushes the page only when the window is ≥1280px and the page
 * keeps ≥720px beside it (rail, bid menu and dock subtracted); otherwise it
 * overlays the page; below 768px it is full-screen. Open state and width persist per browser user.
 */

import { useCallback, useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { History, Maximize2, SquarePen, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { ResizeHandle } from "@/components/ui/ResizeHandle"
import { GradientSparkleIcon } from "@/components/extraction/GradientSparkleIcon"
import { TenderChat } from "@/components/tender-chat/TenderChat"
import { TenderChatHistorySidebar } from "@/components/tender-chat/TenderChatHistorySidebar"
import { ChatHistorySidebar } from "@/components/assistant/ChatHistorySidebar"
import { ChatMessageArea } from "@/components/assistant/ChatMessageArea"
import { ChatInput } from "@/components/assistant/ChatInput"
import { ChatErrorBanner } from "@/components/chat/ChatErrorBanner"
import { ArtifactSurfaceContext } from "@/components/assistant/artifact-surface"
import { useAssistantChatLifecycle } from "@/hooks/useAssistantChatLifecycle"
import { useIsRoleLoaded, useIsViewOnly } from "@/hooks/useViewOnly"
import { useAssistantDockStore } from "@/stores/assistant-dock-store"
import { useAssistantStore } from "@/stores/assistant-store"
import { useTenderChatStore } from "@/stores/tender-chat-store"
import { useBidsStore } from "@/stores/bids-store"
import { useBidMenuStore } from "@/stores/bid-menu-store"
import {
  clampDockWidth,
  dockLayoutFor,
  getAssistantDockMode,
  maxDockWidthFor,
  BID_MENU_COLLAPSED_WIDTH,
  BID_MENU_EXPANDED_WIDTH,
  DOCKED_MIN_VIEWPORT,
  MIN_DOCK_WIDTH,
  type AssistantDockMode,
} from "@/lib/assistant-dock"
import { cn } from "@/lib/utils"

// ===================
// Constants
// ===================

export const ASSISTANT_DOCK_ID = "assistant-dock"
const KEYBOARD_RESIZE_STEP = 16

// ===================
// Hooks
// ===================

function useViewportWidth(): number {
  const [width, setWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : DOCKED_MIN_VIEWPORT
  )
  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth)
    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])
  return width
}

function useDockMode(): AssistantDockMode {
  const { pathname } = useLocation()
  return getAssistantDockMode(pathname)
}

/**
 * `?assistant=open` on any page opens the dock, then drops the param (replace)
 * so a refresh or a shared link doesn't keep re-opening it. The retired
 * `/chat` tab redirects to Overview with this param.
 */
function useAssistantOpenParam(): void {
  const location = useLocation()
  const navigate = useNavigate()
  const setOpen = useAssistantDockStore((s) => s.setOpen)

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get("assistant") !== "open") return
    setOpen(true)
    params.delete("assistant")
    const clean = params.toString()
    void navigate(
      { pathname: location.pathname, search: clean ? `?${clean}` : "", hash: location.hash },
      { replace: true, state: location.state as unknown }
    )
  }, [location.search, location.pathname, location.hash, location.state, navigate, setOpen])
}

// ===================
// Top-bar toggle
// ===================

/** The "Assistant" button at the right end of the top bar. Absent where the dock is hidden. */
export function AssistantDockToggle() {
  const mode = useDockMode()
  const isOpen = useAssistantDockStore((s) => s.isOpen)
  const toggle = useAssistantDockStore((s) => s.toggle)
  // Every dock chat needs a session the backend refuses a view-only user.
  const isViewOnly = useIsViewOnly()
  const isRoleLoaded = useIsRoleLoaded()

  if (mode.kind === "hidden" || isViewOnly || !isRoleLoaded) return null

  return (
    <Button
      variant={isOpen ? "ghost" : "ai-ghost"}
      size="sm"
      aria-label="Assistant panel"
      aria-expanded={isOpen}
      aria-controls={isOpen ? ASSISTANT_DOCK_ID : undefined}
      onClick={toggle}
      className={cn("h-8 gap-1.5 px-2", isOpen && "bg-foreground/[0.07]")}
    >
      <GradientSparkleIcon className="h-4 w-4" />
      <span className="hidden sm:inline">Assistant</span>
    </Button>
  )
}

// ===================
// Dock
// ===================

export function AssistantDock() {
  useAssistantOpenParam()
  const mode = useDockMode()
  const isOpen = useAssistantDockStore((s) => s.isOpen)
  const isViewOnly = useIsViewOnly()
  const isRoleLoaded = useIsRoleLoaded()

  if (mode.kind === "hidden" || !isOpen || isViewOnly || !isRoleLoaded) return null
  return <AssistantDockPanel mode={mode} />
}

function AssistantDockPanel({ mode }: { mode: Exclude<AssistantDockMode, { kind: "hidden" }> }) {
  const navigate = useNavigate()
  const storedWidth = useAssistantDockStore((s) => s.width)
  const setWidth = useAssistantDockStore((s) => s.setWidth)
  const setOpen = useAssistantDockStore((s) => s.setOpen)
  const viewportWidth = useViewportWidth()
  // The dock is hidden on editor pages, so on a bid page the menu follows the
  // non-editor collapse preference.
  const bidMenuCollapsed = useBidMenuStore((s) => s.collapsed)
  const width = clampDockWidth(storedWidth, viewportWidth)
  const bidMenuWidth =
    mode.kind === "bid" ? (bidMenuCollapsed ? BID_MENU_COLLAPSED_WIDTH : BID_MENU_EXPANDED_WIDTH) : 0
  const layout = dockLayoutFor(viewportWidth, { dockWidth: width, bidMenuWidth })
  const maxWidth = maxDockWidthFor(viewportWidth)
  const [isResizing, setIsResizing] = useState(false)
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)

  const bidId = mode.kind === "bid" ? mode.bidId : null
  const selectedBid = useBidsStore((s) => s.selectedBid)
  const scopedBid = bidId && selectedBid?.id === bidId ? selectedBid : null
  const bidTitle = scopedBid?.title ?? null
  // Same gate the retired Tender Chat tab used: a bid the user cannot edit is read-only.
  const isReadOnly = scopedBid?.canEdit === false

  const startNewBidChat = useTenderChatStore((s) => s.startNewChat)
  const startNewOrgChat = useAssistantStore((s) => s.startNewChat)

  const close = useCallback(() => setOpen(false), [setOpen])

  // A different bid (or leaving bids) starts on the conversation, not history.
  useEffect(() => {
    setIsHistoryOpen(false)
  }, [bidId, mode.kind])

  // Escape closes the overlay and the full-screen panel. Ignored while a
  // dialog, menu or listbox is handling it (citation preview, rename dialog).
  useEffect(() => {
    if (layout === "docked") return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return
      const target = e.target instanceof Element ? e.target : null
      if (target?.closest('[role="dialog"],[role="alertdialog"],[role="menu"],[role="listbox"]')) return
      close()
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [layout, close])

  // Drag: the dock is right-anchored to the window, so its width is the
  // distance from the pointer to the window's right edge.
  useEffect(() => {
    if (!isResizing) return
    const handleMouseMove = (e: MouseEvent) => {
      setWidth(clampDockWidth(window.innerWidth - e.clientX, window.innerWidth))
    }
    const handleMouseUp = () => setIsResizing(false)
    document.addEventListener("mousemove", handleMouseMove)
    document.addEventListener("mouseup", handleMouseUp)
    document.body.style.cursor = "col-resize"
    document.body.style.userSelect = "none"
    return () => {
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
      document.body.style.cursor = ""
      document.body.style.userSelect = ""
    }
  }, [isResizing, setWidth])

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)
  }

  // Keyboard: the handle is on the left edge, so ArrowLeft widens.
  const handleResizeKeyDown = (e: React.KeyboardEvent) => {
    const next =
      e.key === "ArrowLeft" ? width + KEYBOARD_RESIZE_STEP
        : e.key === "ArrowRight" ? width - KEYBOARD_RESIZE_STEP
          : e.key === "Home" ? MIN_DOCK_WIDTH
            : e.key === "End" ? maxWidth
              : null
    if (next === null) return
    e.preventDefault()
    setWidth(clampDockWidth(next, viewportWidth))
  }

  const handleNewChat = () => {
    void (mode.kind === "bid" ? startNewBidChat() : startNewOrgChat())
    setIsHistoryOpen(false)
  }

  const label = mode.kind === "bid" ? "Bid assistant" : "Assistant"
  const showHandle = layout !== "fullscreen"

  return (
    <aside
      id={ASSISTANT_DOCK_ID}
      aria-label={label}
      data-layout={layout}
      className={cn(
        "flex bg-background",
        layout === "docked" && "relative h-full shrink-0",
        layout === "overlay" && "absolute inset-y-0 right-0 z-30 shadow-lg",
        layout === "fullscreen" && "fixed inset-0 z-50"
      )}
      style={layout === "fullscreen" ? undefined : { width }}
    >
      {/* The handle's hairline is the panel's left border. */}
      {showHandle && (
        <ResizeHandle
          onMouseDown={handleResizeStart}
          onDoubleClick={() => setWidth(clampDockWidth(400, viewportWidth))}
          onKeyDown={handleResizeKeyDown}
          isResizing={isResizing}
          aria-label="Resize assistant"
          valueNow={width}
          valueMin={MIN_DOCK_WIDTH}
          valueMax={maxWidth}
        />
      )}
      <div className="flex min-w-0 flex-1 flex-col bg-muted/25">
        <div className="flex h-11 shrink-0 items-center gap-1 border-b border-border pl-4 pr-2">
          <h2 className="min-w-0 flex-1 truncate text-sm">
            <span className="font-medium">{label}</span>
            {mode.kind === "bid" && bidTitle && (
              <span className="text-muted-foreground"> · {bidTitle}</span>
            )}
          </h2>
          {mode.kind === "org" && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0"
                  aria-label="Open full page"
                  onClick={() => void navigate("/app/assistant")}
                >
                  <Maximize2 className="h-4 w-4" aria-hidden="true" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Open full page</TooltipContent>
            </Tooltip>
          )}
          <Button
            variant="ghost"
            size="icon"
            className={cn("h-7 w-7 shrink-0", isHistoryOpen && "bg-foreground/[0.07]")}
            aria-label="Conversations"
            aria-pressed={isHistoryOpen}
            onClick={() => setIsHistoryOpen((open) => !open)}
          >
            <History className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            aria-label="New conversation"
            onClick={handleNewChat}
          >
            <SquarePen className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            aria-label="Close assistant"
            onClick={close}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* The conversation stays mounted under the history view, so the
            composer keeps its draft while the user looks at old threads. */}
        <div className="relative min-h-0 flex-1">
          <div className={cn("h-full", isHistoryOpen && "hidden")}>
            {mode.kind === "bid" ? (
              <TenderChat bidId={mode.bidId} layout="dock" readOnly={isReadOnly} />
            ) : (
              <OrgDockChat />
            )}
          </div>
          {isHistoryOpen && (
            <div className="absolute inset-0 bg-background">
              <div className="h-full bg-muted/25">
                {mode.kind === "bid" ? (
                  <TenderChatHistorySidebar
                    isExpanded
                    onExpandedChange={() => {}}
                    fill
                    onSelected={() => setIsHistoryOpen(false)}
                  />
                ) : (
                  <ChatHistorySidebar
                    isExpanded
                    onExpandedChange={() => {}}
                    fill
                    onSelected={() => setIsHistoryOpen(false)}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </aside>
  )
}

/** The org assistant conversation, laid out for the dock (Assistant page's main column). */
function OrgDockChat() {
  useAssistantChatLifecycle()
  const { messages, error, clearError, sendMessage } = useAssistantStore()
  const hasMessages = messages.length > 0
  const canRetry = messages.some((m) => m.role === "user")

  const handleRetry = () => {
    const lastUserMsg = [...messages].reverse().find((m) => m.role === "user")
    if (lastUserMsg) {
      clearError()
      void sendMessage(lastUserMsg.content)
    }
  }

  return (
    <ArtifactSurfaceContext.Provider value="sheet">
      {/* Same stable composer position and min-h-0 / auto-margin rules as AssistantPage. */}
      <div
        className={cn(
          "flex h-full min-h-0 flex-col",
          !hasMessages && "items-center overflow-y-auto px-4 py-8"
        )}
      >
        <div className={cn(hasMessages ? "flex-1 overflow-hidden" : "mt-auto")}>
          <ChatMessageArea />
        </div>
        <div className={cn(!hasMessages && "w-full mt-8 mb-auto")}>
          <ChatErrorBanner error={error} onRetry={canRetry ? handleRetry : undefined} onDismiss={clearError} />
          <ChatInput inputSurface="card" />
        </div>
      </div>
    </ArtifactSurfaceContext.Provider>
  )
}
